import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useLabels, useRequestData, usePrefs } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { isBrowser } from 'ws-scripts/modules/environment';
import { useCompareVehiclesList } from '../hooks/useCompareVehicles';
import { useCompareLink } from '../hooks/useCompareLink';
import { getVehicleObj } from '../utilities/vehicleTracking';
import { getCompareBarId, getCompareBarDismissId } from '../utilities/compare';
import {
	getCompareTriggerElement,
	clearCompareTriggerElement
} from '../utilities/compareTriggerRef';
import { setCompareBarVisible } from '../features/compare';

const CompareBar = () => {
	const compareVehicles = useCompareVehiclesList();
	const compareLink = useCompareLink();
	const labels = useLabels();
	const { enableCompare } = usePrefs();
	const { widgetName, windowId } = useRequestData();
	const dispatch = useDispatch();

	const [dismissed, setDismissed] = useState(false);
	const [liveText, setLiveText] = useState('');
	const prevCountRef = useRef(compareVehicles?.length || 0);
	const barRef = useRef(null);
	const shouldReturnFocusRef = useRef(false);
	const rafRef = useRef();

	const count = compareVehicles?.length || 0;

	const isHidden =
		!isBrowser || enableCompare !== 'true' || count === 0 || dismissed;

	// Keep Redux visibility flag in sync so CompareButton skip links
	// can hide themselves when the region is not visible.
	useEffect(() => {
		dispatch(setCompareBarVisible(!isHidden));
	}, [isHidden, dispatch]);

	// Return focus after user dismisses (after React commits DOM changes)
	useEffect(() => {
		if (dismissed && shouldReturnFocusRef.current) {
			shouldReturnFocusRef.current = false;
			const triggerEl = getCompareTriggerElement();

			if (triggerEl && typeof triggerEl.focus === 'function') {
				// Use requestAnimationFrame to ensure DOM and accessibility tree are updated
				// This gives screen readers time to process the dismissal before focus returns
				rafRef.current = requestAnimationFrame(() => {
					triggerEl.focus();
				});
			}

			clearCompareTriggerElement();
		}

		return () => {
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
		};
	}, [dismissed]);

	// Reset dismiss and announce count change to screen readers
	useEffect(() => {
		if (prevCountRef.current !== count && count > 0) {
			setDismissed(false);

			const announcement =
				count === 1
					? labels.get('ADD_MORE_VEHICLES_TO_COMPARE')
					: labels
							.get('X_VEHICLES_SELECTED_FOR_COMPARISON')
							.replace('{count}', count);
			setLiveText(announcement);
		}
		if (count === 0) {
			setLiveText(labels.get('NO_VEHICLES_SELECTED_FOR_COMPARISON'));
		}
		prevCountRef.current = count;
	}, [count, labels]);

	const handleDismiss = useCallback(() => {
		shouldReturnFocusRef.current = true;
		setDismissed(true);
		trackEvent(widgetName, windowId, {
			element: 'Compare bar close button',
			result: 'Dismissed compare bar'
		});
	}, [widgetName, windowId]);

	const handleBarBlur = useCallback((e) => {
		const barElement = barRef.current;
		if (barElement && !barElement.contains(e.relatedTarget)) {
			// User navigated away from compare bar without dismissing it.
			// Clear trigger so focus isn't unexpectedly returned if they dismiss later.
			clearCompareTriggerElement();
		}
	}, []);

	const handleKeyDown = useCallback(
		(e) => {
			if (e.key === 'Escape') {
				handleDismiss();
			}
		},
		[handleDismiss]
	);

	const handleCompareLinkClick = useCallback(() => {
		clearCompareTriggerElement();
		trackEvent(widgetName, windowId, {
			element: 'Compare Button',
			result: 'Navigated to comparison page',
			vehicles: compareVehicles.map((id) => getVehicleObj(id))
		});
	}, [widgetName, windowId, compareVehicles]);

	const liveRegion = (
		<span
			className="sr-only"
			role="status"
			aria-live="polite"
			aria-atomic="true"
		>
			{liveText}
		</span>
	);

	if (isHidden) {
		return isBrowser ? createPortal(liveRegion, document.body) : liveRegion;
	}

	const labelTemplate = labels.get('VEHICLES_SELECTED');
	const parts = labelTemplate.split('{0}');
	const messageText =
		count === 1 ? (
			labels.get('ADD_MORE_TO_COMPARE')
		) : (
			<>
				{parts[0]}
				<span className="font-weight-bold">{count}</span>
				{parts[1]}
			</>
		);

	const barId = getCompareBarId(windowId);
	const dismissBtnId = getCompareBarDismissId(windowId);

	const content = (
		<>
			{liveRegion}
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- section needs onKeyDown for Escape to dismiss */}
			<section
				id={barId}
				className="srp-compare-bar position-fixed bg-contrast-med shadow border border-transparent"
				aria-label={labels.get('COMPARE_BAR')}
				ref={barRef}
				onKeyDown={handleKeyDown}
				onBlur={handleBarBlur}
			>
				<button
					id={dismissBtnId}
					type="button"
					className="btn-unstyled float-right line-height-reset p-4 shadow-none"
					onClick={handleDismiss}
					aria-label={labels.get('DISMISS_COMPARE_BAR')}
				>
					<i
						className="ddc-icon ddc-icon-remove2 ddc-icon-size-large"
						aria-hidden="true"
					/>
				</button>
				<div
					className={`text-center ${
						count >= 2 ? 'py-4' : 'py-5'
					} px-7`}
				>
					<span className="ddc-font-size-large">{messageText}</span>
					{count >= 2 && (
						<a
							className="btn btn-primary btn-block mt-4"
							href={compareLink}
							onClick={handleCompareLinkClick}
							aria-label={labels
								.get('COMPARE_X_VEHICLES')
								.replace('{count}', count)}
						>
							{labels.get('COMPARE')}
						</a>
					)}
				</div>
			</section>
		</>
	);

	return createPortal(content, document.body);
};

export default CompareBar;
